import * as THREE from "three";
import { WORLD_HALF } from "@palomitas/shared";
import type { WorldBuildResult } from "./world";

export type HazardEvent =
  | { type: "shoo"; position: THREE.Vector3 }
  | { type: "car"; position: THREE.Vector3 }
  | { type: "bump"; position: THREE.Vector3 }
  | { type: "familyCatch"; position: THREE.Vector3 }
  | { type: "breadcrumbs"; position: THREE.Vector3; count: number }
  | { type: "scavenge"; position: THREE.Vector3; radius: number; maxCount: number };

interface Pedestrian {
  group: THREE.Group;
  target: THREE.Vector3;
  speed: number;
  shooCooldown: number;
  collisionCooldown: number;
}

interface Car {
  group: THREE.Group;
  speed: number;
  direction: 1 | -1;
}

interface BirdFeeder {
  group: THREE.Group;
  armPivot: THREE.Group;
  throwOriginLocal: THREE.Vector3;
  home: THREE.Vector3;
  target: THREE.Vector3;
  walkSpeed: number;
  collisionCooldown: number;
  throwCooldown: number;
  throwAnimTime: number;
}

interface BreadcrumbThrowVisual {
  mesh: THREE.Mesh;
  from: THREE.Vector3;
  to: THREE.Vector3;
  elapsed: number;
  duration: number;
  arcHeight: number;
}

interface ScavengerBird {
  group: THREE.Group;
  target: THREE.Vector3;
  speed: number;
  flapPhase: number;
  stealingCooldown: number;
  mode: "follow" | "steal" | "flee";
  modeTimer: number;
  wanderTimer: number;
  hopCooldown: number;
  hopTime: number;
  hopDuration: number;
  hopFrom: THREE.Vector3;
  hopTo: THREE.Vector3;
  hopArc: number;
}

interface KidWithDonut {
  group: THREE.Group;
  donut: THREE.Mesh;
  home: THREE.Vector3;
  target: THREE.Vector3;
  speed: number;
  circleAngle: number;
  circleRadius: number;
  hasDonut: boolean;
  respawnCooldown: number;
  chaseTimer: number;
  collisionCooldown: number;
  bobPhase: number;
}

interface ParentGuardian {
  group: THREE.Group;
  seat: THREE.Vector3;
  torso: THREE.Mesh;
  lowerLegs: [THREE.Mesh, THREE.Mesh];
  chaseTimer: number;
  speed: number;
  collisionCooldown: number;
}

interface PirateParrot {
  group: THREE.Group;
  hatPivot: THREE.Group;
  // Pivot Object3D containing the dagger geometry; rotated around its grip.
  dagger: THREE.Object3D;
  baseY: number;
  bobPhase: number;
  facingTarget: THREE.Vector3;
  faceTimer: number;
  isFlying: boolean;
  flightTarget: THREE.Vector3;
}

const BIRD_FEEDER_TURN_SPEED = 3.4; // radians/second
const BIRD_FEEDER_THROW_FREQUENCY_MULTIPLIER = 1.3;
const PIRATE_PARROT_FACE_TURN_SPEED = 3.8;
const PIRATE_PARROT_FLIGHT_SPEED = 10.5;
const PEDESTRIAN_HEAD_Y = 1.93;
const BIRD_FEEDER_HEAD_Y = 1.8;
const CAR_TOP_Y = 1.45;
const KID_HEAD_Y = 1.2;
const PARENT_HEAD_Y = 1.62;

export class HazardSystem {
  private readonly pedestrians: Pedestrian[] = [];
  private readonly cars: Car[] = [];
  private readonly birdFeeder: BirdFeeder;
  private readonly scene: THREE.Scene;
  private readonly world: WorldBuildResult;
  private readonly rng: () => number;
  private readonly throwVisuals: BreadcrumbThrowVisual[] = [];
  private readonly scavengerBirds: ScavengerBird[] = [];
  private readonly kidWithDonut: KidWithDonut;
  private readonly parentGuardian: ParentGuardian;
  private readonly pirateParrot: PirateParrot;
  private static readonly PERSON_COLLISION_RADIUS = 0.72;

  constructor(scene: THREE.Scene, world: WorldBuildResult, rng: () => number) {
    this.scene = scene;
    this.world = world;
    this.rng = rng;

    for (let i = 0; i < 5; i++) this.pedestrians.push(this.spawnPedestrian(scene));
    for (let i = 0; i < 3; i++) this.cars.push(this.spawnCar(scene, i));
    this.birdFeeder = this.spawnBirdFeeder(scene);
    for (let i = 0; i < 3; i++) this.scavengerBirds.push(this.spawnScavengerBird(scene, i));
    const family = this.spawnKidAndParent(scene);
    this.kidWithDonut = family.kid;
    this.parentGuardian = family.parent;
    this.pirateParrot = this.spawnPirateParrot(scene);
  }

  /**
   * Step hazard AI. Returns events of interest (shoo, car hit) so the gameplay
   * layer can react without the hazard system knowing about gameplay.
   */
  step(dt: number, pigeonPos: THREE.Vector3, pigeonPecked: boolean): HazardEvent[] {
    const events: HazardEvent[] = [];
    const bounds = this.world.walkableBounds;

    for (const ped of this.pedestrians) {
      const dir = new THREE.Vector3().subVectors(ped.target, ped.group.position);
      if (dir.length() < 0.5) {
        ped.target.set(
          bounds.minX + this.rng() * (bounds.maxX - bounds.minX),
          0,
          bounds.minZ + this.rng() * (bounds.maxZ - bounds.minZ),
        );
        continue;
      }
      dir.normalize();
      ped.group.position.x += dir.x * ped.speed * dt;
      ped.group.position.z += dir.z * ped.speed * dt;
      ped.group.rotation.y = Math.atan2(dir.x, dir.z);

      ped.shooCooldown = Math.max(0, ped.shooCooldown - dt);
      ped.collisionCooldown = Math.max(0, ped.collisionCooldown - dt);
      const dx = ped.group.position.x - pigeonPos.x;
      const dz = ped.group.position.z - pigeonPos.z;
      const d2 = dx * dx + dz * dz;
      const pedestrianHeadY = ped.group.position.y + PEDESTRIAN_HEAD_Y;
      const pigeonCanHitPedestrian = pigeonPos.y <= pedestrianHeadY;
      if (
        d2 < HazardSystem.PERSON_COLLISION_RADIUS ** 2 &&
        ped.collisionCooldown === 0 &&
        pigeonCanHitPedestrian
      ) {
        events.push({ type: "bump", position: ped.group.position.clone() });
        ped.collisionCooldown = 1.2;
      }
      if (d2 < 1.5 * 1.5 && ped.shooCooldown === 0 && pigeonCanHitPedestrian) {
        events.push({ type: "shoo", position: ped.group.position.clone() });
        ped.shooCooldown = 3;
      }
    }

    for (const car of this.cars) {
      car.group.position.x += car.speed * car.direction * dt;
      const limit = WORLD_HALF + 3;
      if (car.group.position.x > limit) car.group.position.x = -limit;
      if (car.group.position.x < -limit) car.group.position.x = limit;

      const dx = car.group.position.x - pigeonPos.x;
      const dz = car.group.position.z - pigeonPos.z;
      if (Math.abs(dx) < 1.6 && Math.abs(dz) < 1 && pigeonPos.y <= car.group.position.y + CAR_TOP_Y) {
        events.push({ type: "car", position: car.group.position.clone() });
      }
    }

    this.stepBirdFeeder(dt, pigeonPos, events);
    this.stepScavengerBirds(dt, pigeonPos, pigeonPecked, events);
    this.stepKidWithDonut(dt, pigeonPos, events);
    this.stepPirateParrot(dt);
    this.stepThrowVisuals(dt);

    return events;
  }

  tryTakeKidDonut(grabberPos: THREE.Vector3): boolean {
    const kid = this.kidWithDonut;
    if (!kid.hasDonut || kid.respawnCooldown > 0) return false;
    const dx = kid.group.position.x - grabberPos.x;
    const dz = kid.group.position.z - grabberPos.z;
    const dy = (kid.group.position.y + 1.02) - grabberPos.y;
    const horizontalD2 = dx * dx + dz * dz;
    if (horizontalD2 > 0.82 * 0.82) return false;
    if (Math.abs(dy) > 0.9) return false;
    kid.hasDonut = false;
    kid.donut.visible = false;
    kid.respawnCooldown = 16 + this.rng() * 6;
    kid.chaseTimer = 7.5;
    this.parentGuardian.chaseTimer = 7.5;
    // Prevent steal + instant collision reclaim in the same overlap moment.
    kid.collisionCooldown = 0.7;
    this.parentGuardian.collisionCooldown = 0.7;
    return true;
  }

  restoreKidDonutAt(position: THREE.Vector3): void {
    const kid = this.kidWithDonut;
    kid.hasDonut = true;
    kid.donut.visible = true;
    kid.respawnCooldown = 0;
    kid.home.set(position.x, 0, position.z);
    kid.target.copy(kid.home);
    kid.group.position.x = position.x;
    kid.group.position.z = position.z;
    kid.chaseTimer = 0;
    this.parentGuardian.chaseTimer = 0;
  }

  private stepKidWithDonut(dt: number, pigeonPos: THREE.Vector3, events: HazardEvent[]): void {
    const kid = this.kidWithDonut;
    const parent = this.parentGuardian;
    kid.chaseTimer = Math.max(0, kid.chaseTimer - dt);
    parent.chaseTimer = Math.max(0, parent.chaseTimer - dt);
    kid.collisionCooldown = Math.max(0, kid.collisionCooldown - dt);
    parent.collisionCooldown = Math.max(0, parent.collisionCooldown - dt);

    if (kid.chaseTimer > 0) {
      this.chaseTarget(kid.group, pigeonPos, kid.speed, dt);
      this.chaseTarget(parent.group, pigeonPos, parent.speed, dt);
      parent.group.position.y = 0;
      parent.torso.rotation.x = 0;
      for (const leg of parent.lowerLegs) {
        leg.position.z = 0.11;
        leg.position.y = 0.17;
      }
    } else {
      // Idle behavior: jog in a small circle around the bench area.
      kid.circleAngle += dt * 1.9;
      kid.target.set(
        kid.home.x + Math.cos(kid.circleAngle) * kid.circleRadius,
        0,
        kid.home.z + Math.sin(kid.circleAngle) * kid.circleRadius,
      );
      this.chaseTarget(kid.group, kid.target, kid.speed * 0.52, dt);
      parent.group.position.x = parent.seat.x;
      parent.group.position.y = parent.seat.y;
      parent.group.position.z = parent.seat.z;
      parent.group.rotation.y = Math.PI * 0.18;
      parent.torso.rotation.x = -0.22;
      for (const leg of parent.lowerLegs) {
        leg.position.z = 0.3;
        leg.position.y = 0.24;
      }
    }

    // Faster jump cadence with short lift and settle. Keep jump absolute
    // relative to ground so it always falls back instead of drifting upward.
    const jumpPulse = Math.max(0, Math.sin(performance.now() * 0.011 + kid.bobPhase));
    kid.group.position.y = jumpPulse * 0.05;
    this.clampToWalkable(kid.group.position);
    this.clampToWalkable(parent.group.position);

    if (kid.chaseTimer > 0) {
      const kidDx = kid.group.position.x - pigeonPos.x;
      const kidDz = kid.group.position.z - pigeonPos.z;
      const kidHeadY = kid.group.position.y + KID_HEAD_Y;
      if (
        kidDx * kidDx + kidDz * kidDz < 0.75 * 0.75 &&
        kid.collisionCooldown === 0 &&
        pigeonPos.y <= kidHeadY
      ) {
        events.push({ type: "bump", position: kid.group.position.clone() });
        events.push({ type: "familyCatch", position: kid.group.position.clone() });
        kid.collisionCooldown = 1.2;
      }

      const parentDx = parent.group.position.x - pigeonPos.x;
      const parentDz = parent.group.position.z - pigeonPos.z;
      const parentHeadY = parent.group.position.y + PARENT_HEAD_Y;
      if (
        parentDx * parentDx + parentDz * parentDz < 0.82 * 0.82 &&
        parent.collisionCooldown === 0 &&
        pigeonPos.y <= parentHeadY
      ) {
        events.push({ type: "bump", position: parent.group.position.clone() });
        events.push({ type: "familyCatch", position: parent.group.position.clone() });
        parent.collisionCooldown = 1.2;
      }
    }

    kid.respawnCooldown = Math.max(0, kid.respawnCooldown - dt);
    if (!kid.hasDonut && kid.respawnCooldown === 0) {
      kid.hasDonut = true;
      kid.donut.visible = true;
    }
  }

  private chaseTarget(actor: THREE.Group, target: THREE.Vector3, speed: number, dt: number): void {
    const toTarget = new THREE.Vector3().subVectors(target, actor.position);
    toTarget.y = 0;
    const dist = toTarget.length();
    if (dist <= 0.05) return;
    toTarget.multiplyScalar(1 / Math.max(0.001, dist));
    actor.position.x += toTarget.x * speed * dt;
    actor.position.z += toTarget.z * speed * dt;
    actor.rotation.y = Math.atan2(toTarget.x, toTarget.z);
  }

  private clampToWalkable(position: THREE.Vector3): void {
    const b = this.world.walkableBounds;
    position.x = Math.max(b.minX, Math.min(b.maxX, position.x));
    position.z = Math.max(b.minZ, Math.min(b.maxZ, position.z));
  }

  private stepBirdFeeder(dt: number, pigeonPos: THREE.Vector3, events: HazardEvent[]): void {
    const feeder = this.birdFeeder;
    const toTarget = new THREE.Vector3().subVectors(feeder.target, feeder.group.position);
    if (toTarget.lengthSq() < 0.2 * 0.2) {
      feeder.target.copy(this.pickBirdFeederTarget(feeder.home, 2.2));
    } else {
      toTarget.y = 0;
      toTarget.normalize();
      feeder.group.position.x += toTarget.x * feeder.walkSpeed * dt;
      feeder.group.position.z += toTarget.z * feeder.walkSpeed * dt;
      const desiredYaw = Math.atan2(toTarget.x, toTarget.z);
      feeder.group.rotation.y = rotateYawToward(
        feeder.group.rotation.y,
        desiredYaw,
        BIRD_FEEDER_TURN_SPEED * dt,
      );
    }

    feeder.throwCooldown = Math.max(0, feeder.throwCooldown - dt);
    feeder.collisionCooldown = Math.max(0, feeder.collisionCooldown - dt);
    feeder.throwAnimTime = Math.max(0, feeder.throwAnimTime - dt);

    const throwAnimDuration = 0.45;
    const throwT = feeder.throwAnimTime > 0 ? 1 - feeder.throwAnimTime / throwAnimDuration : 0;
    const throwSwing = throwT > 0 ? Math.sin(throwT * Math.PI) : 0;
    feeder.armPivot.rotation.z = -0.35 - throwSwing * 0.95;
    feeder.armPivot.rotation.x = -throwSwing * 0.3;

    const dx = feeder.group.position.x - pigeonPos.x;
    const dz = feeder.group.position.z - pigeonPos.z;
    const d2 = dx * dx + dz * dz;
    const feederHeadY = feeder.group.position.y + BIRD_FEEDER_HEAD_Y;
    if (
      d2 < HazardSystem.PERSON_COLLISION_RADIUS ** 2 &&
      feeder.collisionCooldown === 0 &&
      pigeonPos.y <= feederHeadY
    ) {
      events.push({ type: "bump", position: feeder.group.position.clone() });
      feeder.collisionCooldown = 1.2;
    }

    if (feeder.throwCooldown > 0) return;

    const count = 4 + Math.floor(this.rng() * 3);
    const target = this.getBreadcrumbDropTarget(feeder);
    events.push({
      type: "breadcrumbs",
      position: target,
      count,
    });

    this.spawnThrowVisual(feeder, target, count);
    this.triggerScavengerRush(target);
    feeder.throwAnimTime = throwAnimDuration;
    feeder.throwCooldown = (9 + this.rng() * 4) / BIRD_FEEDER_THROW_FREQUENCY_MULTIPLIER;
  }

  private triggerScavengerRush(target: THREE.Vector3): void {
    for (const bird of this.scavengerBirds) {
      bird.mode = "steal";
      bird.modeTimer = 3 + this.rng() * 1.6;
      const angle = this.rng() * Math.PI * 2;
      const spread = 0.2 + this.rng() * 0.55;
      bird.target.set(
        target.x + Math.cos(angle) * spread,
        0.18 + this.rng() * 0.08,
        target.z + Math.sin(angle) * spread,
      );
      bird.speed = 1.6 + this.rng() * 0.26;
    }
  }

  private stepScavengerBirds(
    dt: number,
    pigeonPos: THREE.Vector3,
    pigeonPecked: boolean,
    events: HazardEvent[],
  ): void {
    let closestPeckedBird: ScavengerBird | null = null;
    if (pigeonPecked) {
      let bestD2 = 1.2 * 1.2;
      for (const bird of this.scavengerBirds) {
        const dx = bird.group.position.x - pigeonPos.x;
        const dz = bird.group.position.z - pigeonPos.z;
        const d2 = dx * dx + dz * dz;
        if (d2 < bestD2) {
          bestD2 = d2;
          closestPeckedBird = bird;
        }
      }
    }

    for (const bird of this.scavengerBirds) {
      bird.stealingCooldown = Math.max(0, bird.stealingCooldown - dt);
      bird.modeTimer = Math.max(0, bird.modeTimer - dt);
      bird.wanderTimer = Math.max(0, bird.wanderTimer - dt);
      bird.hopCooldown = Math.max(0, bird.hopCooldown - dt);
      bird.hopTime = Math.max(0, bird.hopTime - dt);

      if (closestPeckedBird === bird) {
        const toPigeon = new THREE.Vector3().subVectors(bird.group.position, pigeonPos);
        const away = toPigeon.lengthSq() > 1e-4 ? toPigeon.normalize() : new THREE.Vector3(1, 0, 0);
        bird.mode = "flee";
        bird.modeTimer = 0.9 + this.rng() * 0.35;
        bird.speed = 2.4 + this.rng() * 0.4;
        bird.hopTime = 0;
        bird.hopCooldown = 0;
        bird.target.set(
          bird.group.position.x + away.x * (1.6 + this.rng() * 0.8),
          0.85 + this.rng() * 0.18,
          bird.group.position.z + away.z * (1.6 + this.rng() * 0.8),
        );
      }

      if ((bird.mode === "steal" || bird.mode === "flee") && bird.modeTimer === 0) {
        bird.mode = "follow";
        bird.speed = 0.8 + this.rng() * 0.26;
        bird.wanderTimer = 0;
      }

      if (bird.mode === "follow") {
        if (bird.wanderTimer === 0) {
          const angle = this.rng() * Math.PI * 2;
          const dist = this.rng() * 2.1;
          bird.target.set(
            this.birdFeeder.group.position.x + Math.cos(angle) * dist,
            0.28 + this.rng() * 0.22,
            this.birdFeeder.group.position.z + Math.sin(angle) * dist,
          );
          bird.wanderTimer = 0.6 + this.rng() * 1.1;
        }

        // Sparrow-like locomotion: short hops with tiny pauses.
        if (bird.hopTime === 0 && bird.hopCooldown === 0) {
          bird.hopFrom.copy(bird.group.position);
          const toward = new THREE.Vector3().subVectors(bird.target, bird.group.position);
          if (toward.lengthSq() > 1e-4) toward.normalize();
          const yawJitter = (this.rng() - 0.5) * 0.9;
          const dir = new THREE.Vector3(
            toward.x * Math.cos(yawJitter) - toward.z * Math.sin(yawJitter),
            0,
            toward.x * Math.sin(yawJitter) + toward.z * Math.cos(yawJitter),
          );
          const hopDist = 0.12 + this.rng() * 0.2;
          bird.hopTo.set(
            bird.group.position.x + dir.x * hopDist,
            0.2 + this.rng() * 0.06,
            bird.group.position.z + dir.z * hopDist,
          );
          bird.hopDuration = 0.12 + this.rng() * 0.08;
          bird.hopArc = 0.05 + this.rng() * 0.035;
          bird.hopTime = bird.hopDuration;
          bird.hopCooldown = 0.08 + this.rng() * 0.12;
        }
      }

      const toTarget = new THREE.Vector3().subVectors(bird.target, bird.group.position);
      const dist = toTarget.length();
      if (bird.mode === "follow" && bird.hopTime > 0) {
        const hopT = 1 - bird.hopTime / Math.max(0.001, bird.hopDuration);
        bird.group.position.lerpVectors(bird.hopFrom, bird.hopTo, hopT);
        bird.group.position.y += Math.sin(hopT * Math.PI) * bird.hopArc;
        const hopDir = new THREE.Vector3().subVectors(bird.hopTo, bird.hopFrom);
        if (hopDir.lengthSq() > 1e-4) bird.group.rotation.y = Math.atan2(hopDir.x, hopDir.z);
      } else if (dist > 0.05) {
        toTarget.normalize();
        bird.group.position.x += toTarget.x * bird.speed * dt;
        bird.group.position.y += toTarget.y * bird.speed * dt;
        bird.group.position.z += toTarget.z * bird.speed * dt;
        bird.group.rotation.y = Math.atan2(toTarget.x, toTarget.z);
      }

      bird.group.position.y += Math.sin(performance.now() * 0.012 + bird.flapPhase) * 0.0018;

      if (bird.mode === "steal" && dist < 0.55 && bird.stealingCooldown === 0) {
        events.push({
          type: "scavenge",
          position: bird.group.position.clone(),
          radius: 0.55,
          maxCount: 1,
        });
        bird.stealingCooldown = 0.35;
      }
    }
  }

  private pickBirdFeederTarget(home: THREE.Vector3, radius: number): THREE.Vector3 {
    const angle = this.rng() * Math.PI * 2;
    const dist = this.rng() * radius;
    return new THREE.Vector3(
      home.x + Math.cos(angle) * dist,
      0,
      home.z + Math.sin(angle) * dist,
    );
  }

  private getBreadcrumbDropTarget(feeder: BirdFeeder): THREE.Vector3 {
    const yaw = feeder.group.rotation.y + (this.rng() - 0.5) * 0.28;
    const forward = new THREE.Vector3(Math.sin(yaw), 0, Math.cos(yaw));
    return new THREE.Vector3(
      feeder.group.position.x + forward.x * 1.0,
      0,
      feeder.group.position.z + forward.z * 1.0,
    );
  }

  private spawnThrowVisual(feeder: BirdFeeder, target: THREE.Vector3, count: number): void {
    const from = feeder.throwOriginLocal.clone();
    feeder.group.localToWorld(from);
    const visualCount = Math.min(5, Math.max(2, count));

    for (let i = 0; i < visualCount; i++) {
      const mesh = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.03, 0),
        new THREE.MeshLambertMaterial({ color: 0xd6b36a }),
      );
      mesh.castShadow = true;
      this.scene.add(mesh);

      const angle = this.rng() * Math.PI * 2;
      const spread = this.rng() * 0.2;
      const to = new THREE.Vector3(
        target.x + Math.cos(angle) * spread,
        target.y + 0.02,
        target.z + Math.sin(angle) * spread,
      );

      this.throwVisuals.push({
        mesh,
        from: from.clone(),
        to,
        elapsed: 0,
        duration: 0.32 + this.rng() * 0.1,
        arcHeight: 0.45 + this.rng() * 0.18,
      });
    }
  }

  private stepThrowVisuals(dt: number): void {
    for (let i = this.throwVisuals.length - 1; i >= 0; i--) {
      const throwFx = this.throwVisuals[i];
      if (!throwFx) continue;

      throwFx.elapsed += dt;
      const t = Math.min(1, throwFx.elapsed / throwFx.duration);
      throwFx.mesh.position.lerpVectors(throwFx.from, throwFx.to, t);
      throwFx.mesh.position.y += Math.sin(t * Math.PI) * throwFx.arcHeight;
      throwFx.mesh.rotation.x += dt * 8;
      throwFx.mesh.rotation.z += dt * 10;

      if (t >= 1) {
        this.scene.remove(throwFx.mesh);
        this.throwVisuals.splice(i, 1);
      }
    }
  }

  private stepPirateParrot(dt: number): void {
    const parrot = this.pirateParrot;
    const now = performance.now();
    if (parrot.isFlying) {
      const toTarget = new THREE.Vector3().subVectors(parrot.flightTarget, parrot.group.position);
      const dist = toTarget.length();
      if (dist <= PIRATE_PARROT_FLIGHT_SPEED * dt) {
        parrot.group.position.copy(parrot.flightTarget);
        parrot.baseY = parrot.flightTarget.y;
        parrot.isFlying = false;
      } else if (dist > 1e-5) {
        toTarget.multiplyScalar(1 / dist);
        parrot.group.position.addScaledVector(toTarget, PIRATE_PARROT_FLIGHT_SPEED * dt);
        const targetYaw = Math.atan2(-toTarget.x, -toTarget.z);
        parrot.group.rotation.y = rotateYawToward(
          parrot.group.rotation.y,
          targetYaw,
          PIRATE_PARROT_FACE_TURN_SPEED * dt * 1.6,
        );
      }
    } else {
      parrot.group.position.y = parrot.baseY + Math.sin(now * 0.0038 + parrot.bobPhase) * 0.05;
    }
    parrot.faceTimer = Math.max(0, parrot.faceTimer - dt);
    if (parrot.faceTimer > 0) {
      const toTarget = new THREE.Vector3().subVectors(parrot.facingTarget, parrot.group.position);
      toTarget.y = 0;
      if (toTarget.lengthSq() > 1e-4) {
        // The parrot model's visual forward points toward -Z, so to "look at"
        // a target we use yaw derived from the negated target direction.
        const targetYaw = Math.atan2(-toTarget.x, -toTarget.z);
        parrot.group.rotation.y = rotateYawToward(
          parrot.group.rotation.y,
          targetYaw,
          PIRATE_PARROT_FACE_TURN_SPEED * dt,
        );
      }
    }
    parrot.hatPivot.rotation.z = Math.sin(now * 0.0026 + parrot.bobPhase * 0.7) * 0.08;
    parrot.dagger.rotation.z = -0.18 + Math.sin(now * 0.005 + parrot.bobPhase) * 0.16;
  }

  setPirateParrotSpeakingTo(target: THREE.Vector3, holdSeconds = 2): void {
    this.pirateParrot.facingTarget.set(target.x, this.pirateParrot.group.position.y, target.z);
    this.pirateParrot.faceTimer = Math.max(0.1, holdSeconds);
  }

  flyPirateParrotTo(target: THREE.Vector3): void {
    const parrot = this.pirateParrot;
    parrot.flightTarget.copy(target);
    parrot.baseY = target.y;
    parrot.isFlying = true;
    parrot.faceTimer = 0;
  }

  getPirateParrotPosition(): THREE.Vector3 {
    return this.pirateParrot.group.position.clone();
  }

  private spawnPedestrian(scene: THREE.Scene): Pedestrian {
    const group = new THREE.Group();
    const colors = [0x4a6fa5, 0xa55b3c, 0x7a4d8e, 0x3c8a5f, 0xd48c3c];
    const shirtColor = colors[Math.floor(this.rng() * colors.length)] ?? 0x4a6fa5;
    const pantsColor = 0x2a2a2a;
    const skinColor = 0xe0b080;

    const pants = new THREE.Mesh(
      new THREE.CylinderGeometry(0.2, 0.18, 0.9, 8),
      new THREE.MeshLambertMaterial({ color: pantsColor }),
    );
    pants.position.y = 0.45;
    pants.castShadow = true;
    group.add(pants);

    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.25, 0.22, 0.7, 8),
      new THREE.MeshLambertMaterial({ color: shirtColor }),
    );
    torso.position.y = 1.25;
    torso.castShadow = true;
    group.add(torso);

    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 12, 10),
      new THREE.MeshLambertMaterial({ color: skinColor }),
    );
    head.position.y = 1.75;
    head.castShadow = true;
    group.add(head);

    const bounds = this.world.walkableBounds;
    group.position.set(
      bounds.minX + this.rng() * (bounds.maxX - bounds.minX),
      0,
      bounds.minZ + this.rng() * (bounds.maxZ - bounds.minZ),
    );

    scene.add(group);

    return {
      group,
      target: new THREE.Vector3(
        bounds.minX + this.rng() * (bounds.maxX - bounds.minX),
        0,
        bounds.minZ + this.rng() * (bounds.maxZ - bounds.minZ),
      ),
      speed: 0.8 + this.rng() * 0.5,
      shooCooldown: 0,
      collisionCooldown: 0,
    };
  }

  private spawnCar(scene: THREE.Scene, index: number): Car {
    const group = new THREE.Group();
    const colors = [0xc94c4c, 0x2e6dbb, 0xe8c547, 0x4a5c3e];
    const bodyColor = colors[index % colors.length] ?? 0xc94c4c;

    const body = new THREE.Mesh(
      new THREE.BoxGeometry(2.4, 0.7, 1.2),
      new THREE.MeshLambertMaterial({ color: bodyColor }),
    );
    body.position.y = 0.6;
    body.castShadow = true;
    group.add(body);

    const cabin = new THREE.Mesh(
      new THREE.BoxGeometry(1.4, 0.55, 1.1),
      new THREE.MeshLambertMaterial({ color: bodyColor }),
    );
    cabin.position.set(-0.1, 1.15, 0);
    cabin.castShadow = true;
    group.add(cabin);

    const windowMat = new THREE.MeshBasicMaterial({ color: 0x0f1b2a });
    for (const [x, z] of [[0.62, 0], [-0.62, 0]]) {
      const win = new THREE.Mesh(new THREE.PlaneGeometry(0.3, 0.4), windowMat);
      win.position.set(x ?? 0, 1.15, (z ?? 0) + 0.56);
      group.add(win);
    }

    const wheelMat = new THREE.MeshLambertMaterial({ color: 0x111111 });
    for (const [x, z] of [[0.8, 0.55], [-0.8, 0.55], [0.8, -0.55], [-0.8, -0.55]]) {
      const wheel = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.24, 0.2, 12), wheelMat);
      wheel.rotation.z = Math.PI / 2;
      wheel.position.set(x ?? 0, 0.24, z ?? 0);
      group.add(wheel);
    }

    const direction: 1 | -1 = this.rng() > 0.5 ? 1 : -1;
    group.position.set((this.rng() * 2 - 1) * WORLD_HALF, 0, this.world.road.z);
    group.rotation.y = direction === 1 ? 0 : Math.PI;

    scene.add(group);

    return {
      group,
      speed: 7 + this.rng() * 4,
      direction,
    };
  }

  private spawnBirdFeeder(scene: THREE.Scene): BirdFeeder {
    const group = new THREE.Group();

    const skirt = new THREE.Mesh(
      new THREE.CylinderGeometry(0.24, 0.34, 0.8, 10),
      new THREE.MeshLambertMaterial({ color: 0x4f3f5b }),
    );
    skirt.position.y = 0.4;
    skirt.castShadow = true;
    group.add(skirt);

    const torso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.22, 0.25, 0.72, 10),
      new THREE.MeshLambertMaterial({ color: 0x7f5f4a }),
    );
    torso.position.y = 1.12;
    torso.castShadow = true;
    group.add(torso);

    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 12, 10),
      new THREE.MeshLambertMaterial({ color: 0xe4bf9a }),
    );
    head.position.y = 1.62;
    head.castShadow = true;
    group.add(head);

    const hair = new THREE.Mesh(
      new THREE.SphereGeometry(0.2, 12, 10),
      new THREE.MeshLambertMaterial({ color: 0xb7b7b7 }),
    );
    hair.position.y = 1.69;
    hair.scale.set(1.05, 0.8, 1.05);
    hair.castShadow = true;
    group.add(hair);

    const armPivot = new THREE.Group();
    armPivot.position.set(0.2, 1.34, 0.03);
    group.add(armPivot);

    const arm = new THREE.Mesh(
      new THREE.BoxGeometry(0.12, 0.5, 0.12),
      new THREE.MeshLambertMaterial({ color: 0x7f5f4a }),
    );
    arm.position.set(0.1, -0.2, 0);
    arm.rotation.z = -0.35;
    arm.castShadow = true;
    armPivot.add(arm);

    const satchel = new THREE.Mesh(
      new THREE.BoxGeometry(0.24, 0.18, 0.12),
      new THREE.MeshLambertMaterial({ color: 0x9a7a4f }),
    );
    satchel.position.set(0.22, 0.95, 0.18);
    satchel.rotation.y = 0.25;
    satchel.castShadow = true;
    group.add(satchel);

    const home = new THREE.Vector3(6, 0, 8);
    group.position.copy(home);
    group.rotation.y = -Math.PI * 0.35;
    scene.add(group);

    return {
      group,
      armPivot,
      throwOriginLocal: new THREE.Vector3(0.37, 1.18, 0.12),
      home,
      target: this.pickBirdFeederTarget(home, 2.2),
      walkSpeed: 0.45,
      collisionCooldown: 0,
      throwCooldown: (3 + this.rng() * 1.8) / BIRD_FEEDER_THROW_FREQUENCY_MULTIPLIER,
      throwAnimTime: 0,
    };
  }

  private spawnScavengerBird(scene: THREE.Scene, index: number): ScavengerBird {
    const group = new THREE.Group();

    const body = new THREE.Mesh(
      new THREE.SphereGeometry(0.09, 10, 8),
      new THREE.MeshLambertMaterial({ color: 0x5a6470 }),
    );
    body.scale.set(1.2, 0.8, 1.5);
    body.castShadow = true;
    group.add(body);

    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.055, 10, 8),
      new THREE.MeshLambertMaterial({ color: 0x646f7d }),
    );
    head.position.set(0, 0.02, -0.11);
    head.castShadow = true;
    group.add(head);

    const beak = new THREE.Mesh(
      new THREE.ConeGeometry(0.022, 0.08, 6),
      new THREE.MeshLambertMaterial({ color: 0xd2a55b }),
    );
    beak.rotation.x = Math.PI / 2;
    beak.position.set(0, 0.01, -0.16);
    group.add(beak);

    const wingMat = new THREE.MeshLambertMaterial({ color: 0x48515c });
    for (const side of [-1, 1]) {
      const wing = new THREE.Mesh(new THREE.BoxGeometry(0.14, 0.02, 0.08), wingMat);
      wing.position.set(side * 0.08, 0, -0.01);
      wing.rotation.z = side * 0.35;
      group.add(wing);
    }

    group.scale.setScalar(0.5);
    const angle = this.rng() * Math.PI * 2;
    const radius = 0.2 + this.rng() * 1.4;
    group.position.set(
      this.birdFeeder.group.position.x + Math.cos(angle) * radius,
      0.24 + this.rng() * 0.16,
      this.birdFeeder.group.position.z + Math.sin(angle) * radius,
    );
    scene.add(group);

    return {
      group,
      target: group.position.clone(),
      speed: 0.8 + this.rng() * 0.26,
      flapPhase: this.rng() * Math.PI * 2,
      stealingCooldown: 0,
      mode: "follow",
      modeTimer: 0,
      wanderTimer: this.rng() * 0.8 + index * 0.05,
      hopCooldown: this.rng() * 0.15,
      hopTime: 0,
      hopDuration: 0.16,
      hopFrom: group.position.clone(),
      hopTo: group.position.clone(),
      hopArc: 0.06,
    };
  }

  private spawnPirateParrot(scene: THREE.Scene): PirateParrot {
    // Coordinate convention: parrot faces -Z (matches the pigeon).
    // +X = parrot's right, -X = parrot's left. Origin at the floor between feet.
    // Total height is tuned to match a level-5 pigeon (head ~0.50, hat top ~0.62).
    const group = new THREE.Group();

    const greenMat = new THREE.MeshLambertMaterial({ color: 0x4ea83c });
    const greenDarkMat = new THREE.MeshLambertMaterial({ color: 0x2f7a26 });
    const greenLightMat = new THREE.MeshLambertMaterial({ color: 0x86d65c });
    const beakMat = new THREE.MeshLambertMaterial({ color: 0xf2c742 });
    const beakLowerMat = new THREE.MeshLambertMaterial({ color: 0xe89230 });
    const eyeWhiteMat = new THREE.MeshLambertMaterial({ color: 0xf6f1df });
    const irisMat = new THREE.MeshLambertMaterial({ color: 0x7a55b8 });
    const pupilMat = new THREE.MeshBasicMaterial({ color: 0x0f0f12 });
    const blackMat = new THREE.MeshLambertMaterial({ color: 0x141414 });
    const woodMat = new THREE.MeshLambertMaterial({ color: 0x8b5a3c });
    const woodDarkMat = new THREE.MeshLambertMaterial({ color: 0x5a3a25 });
    const hatMat = new THREE.MeshLambertMaterial({ color: 0x4f3225 });
    const hatBandMat = new THREE.MeshLambertMaterial({ color: 0x2a1810 });
    const buckleMat = new THREE.MeshLambertMaterial({ color: 0xc99c4f });
    const purpleFeatherMat = new THREE.MeshLambertMaterial({ color: 0xa888d8 });
    const purpleFeatherDarkMat = new THREE.MeshLambertMaterial({ color: 0x7c5cad });
    const orangeFeatherMat = new THREE.MeshLambertMaterial({ color: 0xea6f1d });
    const bladeMat = new THREE.MeshLambertMaterial({ color: 0xdadee6 });

    // --- Body: chubby green pear ---
    const body = new THREE.Mesh(new THREE.SphereGeometry(0.14, 22, 18), greenMat);
    body.scale.set(1.05, 1.45, 1.20);
    body.position.set(0, 0.27, 0.01);
    body.castShadow = true;
    group.add(body);

    // Lighter belly patch (front-lower)
    const belly = new THREE.Mesh(new THREE.SphereGeometry(0.115, 20, 16), greenLightMat);
    belly.scale.set(0.95, 1.2, 0.9);
    belly.position.set(0, 0.24, -0.06);
    belly.castShadow = true;
    group.add(belly);

    // --- Head ---
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.115, 22, 18), greenMat);
    head.scale.set(1.0, 0.98, 1.05);
    head.position.set(0, 0.46, -0.09);
    head.castShadow = true;
    group.add(head);

    // --- Beak (hooked, three pieces: upper, hook tip, lower) ---
    const upperBeak = new THREE.Mesh(new THREE.ConeGeometry(0.062, 0.16, 18), beakMat);
    upperBeak.rotation.x = -Math.PI / 2;
    upperBeak.position.set(0, 0.455, -0.20);
    upperBeak.castShadow = true;
    group.add(upperBeak);

    const beakHook = new THREE.Mesh(new THREE.ConeGeometry(0.04, 0.085, 14), beakMat);
    beakHook.rotation.x = Math.PI;
    beakHook.position.set(0, 0.41, -0.265);
    group.add(beakHook);

    const lowerBeak = new THREE.Mesh(new THREE.ConeGeometry(0.05, 0.09, 14), beakLowerMat);
    lowerBeak.rotation.x = -Math.PI / 2;
    lowerBeak.position.set(0, 0.405, -0.18);
    group.add(lowerBeak);

    // --- Eyes (one each side; large with purple iris) ---
    for (const sx of [-1, 1] as const) {
      const sclera = new THREE.Mesh(new THREE.SphereGeometry(0.034, 14, 12), eyeWhiteMat);
      sclera.position.set(sx * 0.078, 0.495, -0.115);
      group.add(sclera);

      const iris = new THREE.Mesh(new THREE.SphereGeometry(0.022, 12, 10), irisMat);
      iris.position.set(sx * 0.092, 0.495, -0.135);
      group.add(iris);

      const pupil = new THREE.Mesh(new THREE.SphereGeometry(0.011, 10, 8), pupilMat);
      pupil.position.set(sx * 0.099, 0.498, -0.143);
      group.add(pupil);
    }

    // --- Wings (folded, dark green outer with lighter accents) ---
    for (const sx of [-1, 1] as const) {
      const wing = new THREE.Mesh(new THREE.SphereGeometry(0.115, 18, 14), greenDarkMat);
      wing.scale.set(0.45, 1.35, 1.0);
      wing.position.set(sx * 0.135, 0.27, 0.03);
      wing.rotation.set(-0.05, 0, sx * -0.08);
      wing.castShadow = true;
      group.add(wing);

      // Wing tip — long pointed flight feather extending back-down
      const wingTip = new THREE.Mesh(new THREE.CapsuleGeometry(0.032, 0.13, 8, 14), greenDarkMat);
      wingTip.position.set(sx * 0.14, 0.18, 0.10);
      wingTip.rotation.set(-0.45, 0, sx * 0.18);
      wingTip.castShadow = true;
      group.add(wingTip);

      // Lighter green feather streaks on the wing
      for (let i = 0; i < 2; i++) {
        const streak = new THREE.Mesh(
          new THREE.CapsuleGeometry(0.012, 0.07, 4, 8),
          greenLightMat,
        );
        streak.position.set(sx * (0.14 + i * 0.012), 0.30 - i * 0.05, 0.045 + i * 0.02);
        streak.rotation.set(-0.2, 0, sx * 0.1);
        group.add(streak);
      }
    }

    // --- Tail (fan of feathers extending back & slightly down) ---
    for (let i = -1; i <= 1; i++) {
      const tail = new THREE.Mesh(new THREE.CapsuleGeometry(0.022, 0.16, 6, 10), greenDarkMat);
      tail.position.set(i * 0.035, 0.18, 0.16);
      tail.rotation.set(-1.0, 0, i * 0.20);
      tail.castShadow = true;
      group.add(tail);
    }

    // --- Legs ---
    // Right leg (parrot's right, +X): black clawed foot
    const clawLeg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.017, 0.017, 0.14, 10),
      blackMat,
    );
    clawLeg.position.set(0.062, 0.085, 0.04);
    clawLeg.castShadow = true;
    group.add(clawLeg);

    const footPad = new THREE.Mesh(new THREE.SphereGeometry(0.028, 10, 8), blackMat);
    footPad.scale.set(1.4, 0.55, 1.5);
    footPad.position.set(0.062, 0.014, 0.025);
    group.add(footPad);

    for (let t = -1; t <= 1; t++) {
      const toe = new THREE.Mesh(new THREE.CapsuleGeometry(0.011, 0.045, 4, 8), blackMat);
      toe.position.set(0.062 + t * 0.022, 0.012, -0.015);
      toe.rotation.set(Math.PI / 2 - 0.25, 0, t * 0.25);
      group.add(toe);
    }

    // Left leg (parrot's left, -X): wooden peg leg
    const pegLeg = new THREE.Mesh(
      new THREE.CylinderGeometry(0.024, 0.038, 0.18, 14),
      woodMat,
    );
    pegLeg.position.set(-0.06, 0.09, 0.06);
    pegLeg.castShadow = true;
    group.add(pegLeg);

    // Darker wood band where the peg meets the body (strap detail)
    const pegStrap = new THREE.Mesh(new THREE.TorusGeometry(0.028, 0.008, 8, 16), woodDarkMat);
    pegStrap.position.set(-0.06, 0.165, 0.06);
    pegStrap.rotation.x = Math.PI / 2;
    group.add(pegStrap);

    // Peg tip flared base
    const pegTip = new THREE.Mesh(new THREE.CylinderGeometry(0.042, 0.034, 0.018, 14), woodDarkMat);
    pegTip.position.set(-0.06, 0.005, 0.06);
    group.add(pegTip);

    // --- Hat (tricorne pirate hat) ---
    const hatPivot = new THREE.Group();
    hatPivot.position.set(0, 0.555, -0.07);
    hatPivot.rotation.set(0, 0.04, -0.06);
    group.add(hatPivot);

    // Brim: wide oval cylinder (very thin)
    const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.225, 0.022, 26), hatMat);
    brim.scale.set(1.0, 1.0, 1.05);
    brim.castShadow = true;
    hatPivot.add(brim);

    // Tricorne points: three small upturned tips around the brim
    for (const angle of [Math.PI, Math.PI * 0.4, -Math.PI * 0.4]) {
      const tip = new THREE.Mesh(new THREE.ConeGeometry(0.07, 0.075, 14), hatMat);
      tip.position.set(Math.sin(angle) * 0.18, 0.04, Math.cos(angle) * 0.18);
      hatPivot.add(tip);
    }

    // Crown
    const crown = new THREE.Mesh(new THREE.CylinderGeometry(0.135, 0.155, 0.13, 24), hatMat);
    crown.position.y = 0.082;
    crown.castShadow = true;
    hatPivot.add(crown);

    // Crown rounded top
    const crownTop = new THREE.Mesh(new THREE.SphereGeometry(0.135, 18, 14), hatMat);
    crownTop.scale.set(1.0, 0.55, 1.0);
    crownTop.position.y = 0.148;
    crownTop.castShadow = true;
    hatPivot.add(crownTop);

    // Hat band (darker stripe at the crown base)
    const band = new THREE.Mesh(new THREE.CylinderGeometry(0.158, 0.158, 0.024, 26), hatBandMat);
    band.position.y = 0.028;
    hatPivot.add(band);

    // Buckle on the front of the band
    const buckle = new THREE.Mesh(new THREE.BoxGeometry(0.045, 0.028, 0.012), buckleMat);
    buckle.position.set(0, 0.028, -0.158);
    hatPivot.add(buckle);

    // Hat scratch marks (brighter scratches on the crown)
    for (let i = 0; i < 2; i++) {
      const scratch = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.003, 0.05 + i * 0.02, 4, 8),
        new THREE.MeshLambertMaterial({ color: 0xa0786a }),
      );
      scratch.position.set(0.13 - i * 0.03, 0.085 + i * 0.02, -0.06);
      scratch.rotation.set(0, 0.3, 0.4 + i * 0.3);
      hatPivot.add(scratch);
    }

    // Purple feather plume — a fluffy bunch tucked into the hatband
    // Position: front-left of the hat, leaning back and up
    const plumeBase = new THREE.Mesh(
      new THREE.ConeGeometry(0.03, 0.22, 14),
      purpleFeatherDarkMat,
    );
    plumeBase.position.set(-0.08, 0.20, -0.10);
    plumeBase.rotation.set(-0.3, 0.1, 0.5);
    plumeBase.castShadow = true;
    hatPivot.add(plumeBase);

    for (let i = 0; i < 4; i++) {
      const fluff = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.012 + i * 0.002, 0.13 + i * 0.025, 6, 10),
        i < 2 ? purpleFeatherMat : purpleFeatherDarkMat,
      );
      const angle = 0.45 + i * 0.18;
      fluff.position.set(-0.08 - i * 0.012, 0.19 + i * 0.018, -0.09 + i * 0.004);
      fluff.rotation.set(-0.25 - i * 0.06, 0.15 + i * 0.05, angle);
      fluff.castShadow = true;
      hatPivot.add(fluff);
    }

    // Curly orange feather: a couple of small overlapping segments to suggest a curl
    for (let i = 0; i < 3; i++) {
      const curl = new THREE.Mesh(
        new THREE.CapsuleGeometry(0.006, 0.07, 4, 8),
        orangeFeatherMat,
      );
      curl.position.set(-0.05 + i * 0.012, 0.21 + i * 0.018, -0.08 - i * 0.005);
      curl.rotation.set(-0.4 - i * 0.2, 0.2 + i * 0.15, 0.7 - i * 0.25);
      hatPivot.add(curl);
    }

    // --- Dagger (held at the parrot's right side, pivoted at the grip) ---
    const daggerPivot = new THREE.Group();
    daggerPivot.position.set(0.18, 0.18, 0.08);
    daggerPivot.rotation.set(0.25, 0.35, 0); // rotation.z is animated by the loop
    group.add(daggerPivot);

    // Within daggerPivot: blade points DOWN (-Y), handle/grip near origin.
    const pommel = new THREE.Mesh(new THREE.SphereGeometry(0.020, 10, 8), woodDarkMat);
    pommel.position.y = 0.045;
    daggerPivot.add(pommel);

    const handle = new THREE.Mesh(
      new THREE.CylinderGeometry(0.016, 0.016, 0.07, 12),
      woodMat,
    );
    handle.position.y = 0.005;
    handle.castShadow = true;
    daggerPivot.add(handle);

    const guard = new THREE.Mesh(new THREE.BoxGeometry(0.07, 0.012, 0.022), buckleMat);
    guard.position.y = -0.038;
    daggerPivot.add(guard);

    const blade = new THREE.Mesh(new THREE.ConeGeometry(0.022, 0.15, 14), bladeMat);
    blade.rotation.x = Math.PI; // point cone tip downward
    blade.position.y = -0.118;
    blade.castShadow = true;
    daggerPivot.add(blade);

    // Place the parrot near the start path: just right of center and about
    // 5 meters in front of the pigeon's spawn facing direction.
    // baseY is lifted slightly so the bob animation (±0.05 around baseY)
    // keeps the feet from clipping the ground.
    group.position.set(1.4, 0.04, 37);
    group.rotation.y = Math.PI * 0.35;
    scene.add(group);

    return {
      group,
      hatPivot,
      dagger: daggerPivot,
      baseY: group.position.y,
      bobPhase: this.rng() * Math.PI * 2,
      facingTarget: group.position.clone(),
      faceTimer: 0,
      isFlying: false,
      flightTarget: group.position.clone(),
    };
  }

  private spawnKidAndParent(scene: THREE.Scene): { kid: KidWithDonut; parent: ParentGuardian } {
    const bench = new THREE.Group();
    const benchWood = new THREE.MeshLambertMaterial({ color: 0x8b5a3c });
    const benchMetal = new THREE.MeshLambertMaterial({ color: 0x3a3a3a });
    const benchSeat = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.08, 0.45), benchWood);
    benchSeat.position.set(0, 0.45, 0);
    benchSeat.castShadow = true;
    benchSeat.receiveShadow = true;
    bench.add(benchSeat);
    const benchBack = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.5, 0.08), benchWood);
    benchBack.position.set(0, 0.72, -0.2);
    benchBack.castShadow = true;
    bench.add(benchBack);
    for (const side of [-0.72, 0.72]) {
      const leg = new THREE.Mesh(new THREE.BoxGeometry(0.08, 0.45, 0.35), benchMetal);
      leg.position.set(side, 0.22, 0);
      bench.add(leg);
    }
    bench.position.set(-24.5, 0, 13.6);
    bench.rotation.y = Math.PI * 0.18;
    scene.add(bench);

    const group = new THREE.Group();

    const shorts = new THREE.Mesh(
      new THREE.CylinderGeometry(0.14, 0.16, 0.44, 10),
      new THREE.MeshLambertMaterial({ color: 0x2d66a3 }),
    );
    shorts.position.y = 0.22;
    shorts.castShadow = true;
    group.add(shorts);

    const shirt = new THREE.Mesh(
      new THREE.CylinderGeometry(0.17, 0.16, 0.4, 10),
      new THREE.MeshLambertMaterial({ color: 0xe18c42 }),
    );
    shirt.position.y = 0.64;
    shirt.castShadow = true;
    group.add(shirt);

    const head = new THREE.Mesh(
      new THREE.SphereGeometry(0.14, 12, 10),
      new THREE.MeshLambertMaterial({ color: 0xe6c09b }),
    );
    head.position.y = 0.94;
    head.castShadow = true;
    group.add(head);

    const hair = new THREE.Mesh(
      new THREE.SphereGeometry(0.145, 10, 8),
      new THREE.MeshLambertMaterial({ color: 0x5b3a27 }),
    );
    hair.position.set(0, 0.99, 0.015);
    hair.scale.set(1.05, 0.7, 1.05);
    hair.castShadow = true;
    group.add(hair);

    const leftArm = new THREE.Mesh(
      new THREE.BoxGeometry(0.09, 0.3, 0.09),
      new THREE.MeshLambertMaterial({ color: 0xe6c09b }),
    );
    leftArm.position.set(-0.2, 0.64, 0);
    leftArm.rotation.z = 0.18;
    leftArm.castShadow = true;
    group.add(leftArm);

    const rightArm = new THREE.Mesh(
      new THREE.BoxGeometry(0.09, 0.3, 0.09),
      new THREE.MeshLambertMaterial({ color: 0xe6c09b }),
    );
    rightArm.position.set(0.2, 0.64, -0.03);
    rightArm.rotation.z = -0.55;
    rightArm.castShadow = true;
    group.add(rightArm);

    const donut = new THREE.Mesh(
      new THREE.TorusGeometry(0.09, 0.032, 10, 18),
      new THREE.MeshLambertMaterial({ color: 0xf0aed4 }),
    );
    donut.position.set(0.29, 0.58, -0.19);
    donut.rotation.x = Math.PI / 2;
    donut.castShadow = true;
    group.add(donut);

    // Place the kid offset farther from the bench so his idle circle path
    // does not clip through the bench geometry.
    group.scale.setScalar(1.18);
    group.position.set(bench.position.x - 1.85, 0, bench.position.z + 0.9);
    group.rotation.y = -Math.PI * 0.35;
    scene.add(group);

    const parentGroup = new THREE.Group();
    const parentHips = new THREE.Mesh(
      new THREE.SphereGeometry(0.22, 12, 10),
      new THREE.MeshLambertMaterial({ color: 0x34424f }),
    );
    parentHips.scale.set(1.1, 0.58, 1.05);
    parentHips.position.y = 0.52;
    parentHips.castShadow = true;
    parentGroup.add(parentHips);
    let leftLowerLeg: THREE.Mesh | null = null;
    let rightLowerLeg: THREE.Mesh | null = null;
    for (const side of [-1, 1] as const) {
      const upperLeg = new THREE.Mesh(
        new THREE.BoxGeometry(0.14, 0.14, 0.28),
        new THREE.MeshLambertMaterial({ color: 0x34424f }),
      );
      upperLeg.position.set(side * 0.11, 0.44, 0.16);
      upperLeg.castShadow = true;
      parentGroup.add(upperLeg);

      const lowerLeg = new THREE.Mesh(
        new THREE.BoxGeometry(0.11, 0.28, 0.12),
        new THREE.MeshLambertMaterial({ color: 0x2f3c47 }),
      );
      lowerLeg.position.set(side * 0.11, 0.24, 0.3);
      lowerLeg.castShadow = true;
      parentGroup.add(lowerLeg);
      if (side < 0) leftLowerLeg = lowerLeg;
      else rightLowerLeg = lowerLeg;
    }
    const lowerLegs: [THREE.Mesh, THREE.Mesh] = [leftLowerLeg!, rightLowerLeg!];
    const parentTorso = new THREE.Mesh(
      new THREE.CylinderGeometry(0.23, 0.2, 0.78, 10),
      new THREE.MeshLambertMaterial({ color: 0x6d8f5b }),
    );
    parentTorso.position.y = 0.96;
    parentTorso.rotation.x = -0.22;
    parentTorso.castShadow = true;
    parentGroup.add(parentTorso);
    const parentHead = new THREE.Mesh(
      new THREE.SphereGeometry(0.17, 12, 10),
      new THREE.MeshLambertMaterial({ color: 0xe0b080 }),
    );
    parentHead.position.y = 1.47;
    parentHead.castShadow = true;
    parentGroup.add(parentHead);
    const parentHair = new THREE.Mesh(
      new THREE.SphereGeometry(0.18, 12, 8),
      new THREE.MeshLambertMaterial({ color: 0x2e2218 }),
    );
    parentHair.position.set(0, 1.54, 0.02);
    parentHair.scale.set(1.02, 0.72, 1.02);
    parentHair.castShadow = true;
    parentGroup.add(parentHair);
    const seat = new THREE.Vector3(bench.position.x + 0.25, 0.03, bench.position.z + 0.02);
    parentGroup.position.copy(seat);
    parentGroup.rotation.y = Math.PI * 0.18;
    scene.add(parentGroup);

    return {
      kid: {
        group,
        donut,
        home: group.position.clone(),
        target: group.position.clone(),
        speed: 2.2,
        circleAngle: this.rng() * Math.PI * 2,
        circleRadius: 0.42 + this.rng() * 0.34,
        hasDonut: true,
        respawnCooldown: 0,
        chaseTimer: 0,
        collisionCooldown: 0,
        bobPhase: this.rng() * Math.PI * 2,
      },
      parent: {
        group: parentGroup,
        seat,
        torso: parentTorso,
        lowerLegs,
        chaseTimer: 0,
        speed: 1.85,
        collisionCooldown: 0,
      },
    };
  }
}

function rotateYawToward(current: number, target: number, maxStep: number): number {
  const delta = Math.atan2(Math.sin(target - current), Math.cos(target - current));
  if (Math.abs(delta) <= maxStep) return target;
  return current + Math.sign(delta) * maxStep;
}
